import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Login.module.css'
import InputField from '../../components/inputfield/InputField';
import { LoginAPI } from '../../api/API';
import { getAccessToken, clearAccessToken } from '../../api/authTokens';
import CustomButton from '../../components/buttons/CustomButton';
import LogoImage from '../../assets/images/logo-full.png';
import Loading from '../../components/loading/Loading';
import VisibilityOnIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useSystemConfig } from '../../contexts/SystemConfigContext';

function Login({ onSuccess }) {
  const navigate = useNavigate();
  const inputFieldEmailRef = useRef(null);
  const inputFieldPasswordRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const { publicConfig } = useSystemConfig();
  
  // Check if we have public config loaded
  useEffect(() => {
    if (!publicConfig) {
      
    } else {
      
    }
  }, [publicConfig]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'O email é obrigatório';
    }
    if (!emailRegex.test(email)) {
      return 'Digite um email válido';
    }
    return null;
  };

  const validatePassword = (password) => {
    if (!password) {
      return 'A senha é obrigatória';
    }
    return null;
  };

  function ValidateForm() {
    const email = inputFieldEmailRef.current.GetValue();
    const password = inputFieldPasswordRef.current.GetValue();
    
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    if (emailError) {
      inputFieldEmailRef.current.SetError(emailError);
      return false;
    }

    if (passwordError) {
      inputFieldPasswordRef.current.SetError(passwordError);
      return false;
    }

    return inputFieldEmailRef.current.Validate() && inputFieldPasswordRef.current.Validate();
  }

  const doLogin = (email, password, isAutoLogin = false) => {
    if (isLoggingIn) return; // Prevent multiple login attempts
    
    setIsLoggingIn(true);
    setLoading(true);
    setErrorMessage(null); // Clear any previous error messages
    
    if (!isAutoLogin) {
      window.visualLogger?.log('Iniciando login...');
    }

    LoginAPI(email, password, (result) => {
      setIsLoggingIn(false);
      
      if (result.error) {
        window.visualLogger?.error(result.error);
        
        // Set appropriate error message based on the error
        if (result.error == 'Invalid credentials') {
          setErrorMessage('Email ou senha inválidos. Por favor, tente novamente.');
        } else {
          setErrorMessage(result.error);
        }
        
        // If access is denied due to revocation, clear stored credentials
        if (result.error.includes('revogada') || result.error.includes('Acesso negado') || result.code === 'ACCESS_PAUSED') {
          // Limpa apenas o token em memória, mantendo o email lembrado se existir
          clearAccessToken();
        }
        
        setLoading(false);
        return;
      }
      
      if (!isAutoLogin) {
        window.visualLogger?.log('Login realizado com sucesso');
      }
      saveCredentials(email, password);
      // Pass the enhanced session data to parent
      // O refresh token não vem no JSON: o servidor o gravou no cookie HttpOnly
      onSuccess({
        token: result.token,
        session: result.session
      });
      
      // The system will automatically load the private config after login
    });
  }

  // Enviado pelo botão "Entrar" ou pelo Enter em qualquer campo do formulário
  const handleLoginSubmit = (event) => {
    if (event) event.preventDefault();
    if (ValidateForm()) {
      let email = inputFieldEmailRef.current.GetValue();
      let password = inputFieldPasswordRef.current.GetValue();
      doLogin(email, password, false);
    }
  };

  // Armazena apenas o email para a funcionalidade "lembrar-me"
  const saveCredentials = (email, password) => {
    // Armazena apenas o email, nunca a senha
    localStorage.setItem('rememberedEmail', email);
    
    // Armazena um flag indicando que o usuário optou por ser lembrado
    localStorage.setItem('rememberMe', 'true');
    
    // Não armazenamos a senha, apenas confiamos nos tokens JWT
  }

  const getRememberedEmail = () => {
    return localStorage.getItem('rememberedEmail');
  }

  const checkCredentials = () => {
    // Primeiro verifica se já temos um token válido
    if (getAccessToken()) {
      // Já temos um token, não é necessário fazer auto-login
      return;
    }

    // A renovação pelo cookie de refresh já acontece no boot do App (App.js)
        
    // Se não temos token, apenas pré-preenche o email se estiver disponível
    const rememberedEmail = getRememberedEmail();
    if (rememberedEmail && inputFieldEmailRef.current) {
      inputFieldEmailRef.current.SetValue(rememberedEmail);
    }
  }

  useEffect(() => {
    // Check if we're coming from a password reset
    const fromReset = sessionStorage.getItem('from_password_reset');
    
    if (fromReset) {
      // Clear the flag
      sessionStorage.removeItem('from_password_reset');
      // Don't auto-login if coming from password reset
      setLoading(false);
    } else {
      // Normal flow - check credentials
      checkCredentials();
    }
    
    return () => {
      setIsLoggingIn(false); // Cleanup on unmount
    };
  }, []);

  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className='page-content'>
      {loading ? <Loading /> :
        <div className={styles.container}>
          <div className={styles.containerLogin}>
            <div className={styles.logoContainer}>
              <img
                className={styles.logo}
                src={LogoImage}
                alt="Logo"
                onClick={() => navigate('/')}
                style={{ cursor: 'pointer' }}
              />
              <h1 className={styles.title}>Boas vindas!</h1>
              <p className={styles.subtitle}>Entre com suas credenciais para continuar</p>
            </div>
            {errorMessage && (
              <div className={styles.errorMessage}>
                {errorMessage}
              </div>
            )}
            <form className={styles.loginForm} onSubmit={handleLoginSubmit} noValidate>
            <div className={styles.containerInputs}>
              <InputField Width={"100%"} Label={"Email"} type="email" placeholder="Email" ref={inputFieldEmailRef} />
              <div className={styles.containerPassword}>
                <InputField Width={"100%"} Label={"Senha"} type={showPassword ? "text" : "password"} placeholder="Password" ref={inputFieldPasswordRef} />
                {!showPassword ? <VisibilityOnIcon className={styles.buttonVisibility} onClick={handleTogglePasswordVisibility} />
                  : <VisibilityOffIcon className={styles.buttonVisibility} onClick={handleTogglePasswordVisibility} />
                }
              </div>
              <div className={styles.rememberMeContainer}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  defaultChecked={!!localStorage.getItem('rememberMe')}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      localStorage.removeItem('rememberedEmail');
                      localStorage.removeItem('rememberMe');
                    }
                  }}
                />
                <label htmlFor="rememberMe">Lembrar meu email</label>
              </div>
            </div>
            <div className={styles.forgotPassword}>
              <a href="#" onClick={(e) => {
                e.preventDefault();
                // Use replace: true to replace the current URL in history
                // This prevents navigation issues with password reset flow
                navigate('/forgot-password', { replace: true });
              }}>
                Esqueci minha senha
              </a>
            </div>
            <CustomButton variant="contained" style="primary" label={"Entrar"} type="submit" />
            </form>
            <div className={styles.signupLink}>
              Não tem uma conta? <Link to="/">Criar conta</Link>
            </div>
          </div>
        </div>
      }
    </div>
  );
}

export default Login;
